import React from 'react';
import { Link, useNavigate, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getTelehealthServiceBySlug } from '../data/telehealthServices';
import { SITE_URL } from '../siteConfig';
import SEO from './SEO';
import './TelehealthServiceDetail.css';

const fadeLeft = { initial: { opacity: 0, x: -40 }, whileInView: { opacity: 1, x: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };
const fadeRight = { initial: { opacity: 0, x: 40 }, whileInView: { opacity: 1, x: 0 }, viewport: { once: true }, transition: { duration: 0.7, delay: 0.15 } };
const fadeUp = { initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.7 } };

const ServiceBody = ({ service, centered = false }) => (
  <>
    <h2 className="svc-title" style={{ color: service.color }}>{service.heading || 'Care on your schedule'}</h2>
    {service.description.map((p, i) => <p className="svc-desc" key={i}>{p}</p>)}
    {service.conditions && service.conditions.length > 0 && (
      <ul className="svc-conditions">
        {service.conditions.map((c, i) => <li key={i}>{c}</li>)}
      </ul>
    )}
    {service.benefit && (
      <div className="svc-benefit">
        <span className="svc-benefit-label">Why it matters: </span>{service.benefit}
      </div>
    )}
    <p className="svc-desc svc-pricing"><strong>Transparent pricing:</strong> {service.fee} {service.feeDetail} for this service.</p>
    <CtaButton color={service.color} />
  </>
);

const ContentA = ({ service }) => (
  <div className="svc-content svc-content--a">
    <div className="container">
      <div className="svc-two-col">
        <motion.div className="svc-text" {...fadeLeft}>
          <ServiceBody service={service} />
        </motion.div>
        <motion.div className="svc-image-wrap" {...fadeRight}>
          <img src={service.detailImage || service.cardImage} alt={service.title} className="svc-img" />
        </motion.div>
      </div>
    </div>
  </div>
);

const ContentB = ({ service }) => (
  <div className="svc-content svc-content--b">
    <div className="container">
      <div className="svc-two-col svc-two-col--reversed">
        <motion.div className="svc-image-wrap" {...fadeLeft}>
          <img src={service.detailImage || service.cardImage} alt={service.title} className="svc-img" />
        </motion.div>
        <motion.div className="svc-text" {...fadeRight}>
          <ServiceBody service={service} />
        </motion.div>
      </div>
    </div>
  </div>
);

const ContentC = ({ service }) => (
  <div className="svc-content svc-content--c">
    <div className="container">
      <motion.div className="svc-text svc-text--centered" {...fadeUp}>
        <ServiceBody service={service} centered />
      </motion.div>
    </div>
    <motion.div className="svc-wide-image" {...fadeUp} transition={{ duration: 0.8, delay: 0.2 }}>
      <img src={service.cardImage} alt={service.title} className="svc-img--wide" />
    </motion.div>
  </div>
);

const ContentD = ({ service }) => (
  <div className="svc-content--d">
    <motion.div className="svc-d-text" {...fadeLeft}>
      <h2 className="svc-title" style={{ color: service.color }}>{service.heading || 'Care on your schedule'}</h2>
      {service.description.map((p, i) => <p className="svc-desc" key={i}>{p}</p>)}
      {service.conditions && service.conditions.length > 0 && (
        <ul className="svc-conditions">
          {service.conditions.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
      {service.benefit && (
        <div className="svc-benefit">
          <span className="svc-benefit-label">Why it matters: </span>{service.benefit}
        </div>
      )}
      <p className="svc-desc svc-pricing"><strong>Transparent pricing:</strong> {service.fee} {service.feeDetail} for this service.</p>
      <CtaButton color={service.color} />
    </motion.div>
    <motion.div className="svc-d-panel" {...fadeRight}>
      <img src={service.detailImage || service.cardImage} alt={service.title} className="svc-d-img" />
    </motion.div>
  </div>
);

const CtaButton = ({ color }) => {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="svc-btn"
      style={{ background: color, boxShadow: `0 4px 14px ${color}55` }}
      onClick={() => navigate('/contact')}
    >
      Contact us to get started
    </button>
  );
};

const layouts = { a: ContentA, b: ContentB, c: ContentC, d: ContentD };

const TelehealthServiceDetail = () => {
  const { slug } = useParams();
  const service = getTelehealthServiceBySlug(slug);

  if (!service) return <Navigate to="/services" replace />;

  const Layout = layouts[service.layout] || ContentA;
  const canonicalUrl = `${SITE_URL}/services/${service.slug}`;

  return (
    <section className="svc-page">
      <SEO
        title={service.title}
        description={`${service.summary} ${service.fee} ${service.feeDetail}.`}
        keywords={service.keywords}
        canonicalUrl={canonicalUrl}
      />

      <motion.div className="breadcrumb-section" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="container">
          <nav className="breadcrumb-nav">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/services" className="breadcrumb-link">Services</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{service.title}</span>
          </nav>
        </div>
      </motion.div>

      <div
        className="svc-hero"
        style={{ backgroundImage: `url(${service.heroImage})` }}
      >
        <div className="svc-hero-overlay" style={{ background: `linear-gradient(to bottom, ${service.color}99 0%, rgba(0,0,0,0.55) 100%)` }} />
        <motion.div className="svc-hero-content" initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75 }}>
          <h1 className="svc-hero-title">{service.title}</h1>
          <p className="svc-hero-sub">
            <strong>{service.fee}</strong> {service.feeDetail} — fees do not include medication or lab costs unless noted.
          </p>
        </motion.div>
      </div>

      <Layout service={service} />
    </section>
  );
};

export default TelehealthServiceDetail;
